import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { StartMeetingButton, RescheduleCard } from "./BookingZoomActions";
import { WhatsAppFlowStatus } from "./WhatsAppFlowStatus";
import { RefreshCw, Download, Trash2, Calendar, Clock, MapPin, Briefcase, Mail, Phone, Tag, Save, X, Plus, Link as LinkIcon, ChevronLeft, ChevronRight, LayoutGrid, List as ListIcon, Target, Hourglass, Flame, Video, Copy } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "bc_admin_token";
const authHeaders = () => {
  const t = localStorage.getItem(TOKEN_KEY);
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const BOOKING_STATUSES = ["Upcoming", "Completed", "No-Show", "Cancelled"];
const BSTATUS_STYLES = {
  Upcoming: "bg-amber-900/25 border-amber-700/40 text-amber-300",
  Completed: "bg-emerald-900/30 border-emerald-700/50 text-emerald-300",
  "No-Show": "bg-rose-900/25 border-rose-800/40 text-rose-300",
  Cancelled: "bg-zinc-800/50 border-zinc-700 text-zinc-500",
};
const bClass = (s) => BSTATUS_STYLES[s] || BSTATUS_STYLES.Upcoming;

const BOOKING_STAGES = [
  "Meeting Confirmed",
  "Proposal Sent",
  "Payment Link Sent",
  "Onboarded",
  "Followup Required",
  "DNP",
  "JUNK",
];
const STAGE_STYLES = {
  "Meeting Confirmed": "bg-amber-900/25 border-amber-700/40 text-amber-300",
  "Proposal Sent": "bg-sky-900/30 border-sky-700/50 text-sky-300",
  "Payment Link Sent": "bg-violet-900/30 border-violet-700/50 text-violet-300",
  "Onboarded": "bg-emerald-900/40 border-emerald-600/60 text-emerald-300",
  "Followup Required": "bg-yellow-900/30 border-yellow-700/50 text-yellow-300",
  "DNP": "bg-orange-900/25 border-orange-700/50 text-orange-300",
  "JUNK": "bg-rose-900/30 border-rose-700/50 text-rose-300",
};
const stageClass = (s) => STAGE_STYLES[s] || "border-white/10 text-zinc-400";
const formatDate = (iso) => {
  try {
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short", year: "numeric" });
  } catch { return iso; }
};
const formatDt = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit", hour12: true });
  } catch { return iso; }
};

// ============================================================
// Lead Fit Score — prioritise the hottest calls
// ============================================================
function parseAmount(str) {
  if (!str) return 0;
  const s = String(str).toLowerCase();
  let max = 0;
  const re = /([\d,.]+)\s*(cr|crore|lakh|lac|l|k)?/g;
  let m;
  while ((m = re.exec(s)) !== null) {
    let n = parseFloat(m[1].replace(/,/g, ""));
    if (isNaN(n)) continue;
    const u = m[2] || "";
    if (u.startsWith("cr")) n *= 10000000;
    else if (u.startsWith("la") || u === "l") n *= 100000;
    else if (u === "k") n *= 1000;
    if (n > max) max = n;
  }
  return max;
}

const AGE_ORDER = ["0 – 6 months", "6 months – 1 year", "1 – 2 years", "2 – 5 years"];
const normStr = (s) => (s || "").replace(/[–—]/g, "-").replace(/\s+/g, " ").trim().toLowerCase();

function budgetPts(b) {
  const amt = parseAmount(b.ad_budget);
  let pts = amt >= 500000 ? 40 : amt >= 250000 ? 34 : amt >= 100000 ? 28 : amt >= 50000 ? 20 : amt > 0 ? 12 : 0;
  if ((b.running_ads || "").toLowerCase() === "yes") pts = Math.min(40, pts + 4);
  return pts;
}
function goalPts(b) {
  const g = parseAmount(b.goal);
  return g >= 10000000 ? 30 : g >= 5000000 ? 28 : g >= 2500000 ? 26 : g >= 1000000 ? 22 : g >= 500000 ? 16 : g > 0 ? 12 : 0;
}
function agePts(b) {
  const idx = AGE_ORDER.findIndex((a) => normStr(a) === normStr(b.business_age));
  return idx >= 0 ? [5, 10, 15, 20][idx] : 0;
}
function revenuePts(b) {
  const r = parseAmount(b.revenue_band);
  return r >= 2500000 ? 10 : r >= 1000000 ? 8 : r >= 500000 ? 6 : r >= 250000 ? 4 : r >= 100000 ? 3 : r > 0 ? 2 : 0;
}

// Returns { score, tier, label, parts } or null when there's nothing to score.
function computeFit(b) {
  if (!b.ad_budget && !b.goal && !b.business_age && !b.revenue_band) return null;
  const parts = { budget: budgetPts(b), goal: goalPts(b), age: agePts(b), revenue: revenuePts(b) };
  const score = parts.budget + parts.goal + parts.age + parts.revenue;
  const tier = score >= 70 ? "hot" : score >= 45 ? "warm" : "cold";
  const label = tier === "hot" ? "Hot" : tier === "warm" ? "Warm" : "Cold";
  return { score, tier, label, parts };
}

const FIT_STYLES = {
  hot: "bg-red-900/30 border-red-600/60 text-red-300",
  warm: "bg-amber-900/25 border-amber-700/50 text-amber-300",
  cold: "bg-zinc-800/50 border-zinc-700 text-zinc-400",
};

function FitBadge({ fit, size = "sm" }) {
  if (!fit) return null;
  const pad = size === "lg" ? "px-2.5 py-1 text-[11px]" : "px-2 py-0.5 text-[10px]";
  return (
    <span
      className={`inline-flex items-center gap-1 tracking-[0.12em] uppercase border ${FIT_STYLES[fit.tier]} ${pad}`}
      style={{ borderRadius: 0 }}
      data-testid="booking-fit-badge"
      title={`Fit score ${fit.score}/100 · budget ${fit.parts.budget} + goal ${fit.parts.goal} + age ${fit.parts.age} + revenue ${fit.parts.revenue}`}
    >
      {fit.tier === "hot" && <Flame size={11} />} {fit.label} · {fit.score}
    </span>
  );
}

function EmailRow({ label, ts }) {
  const sent = !!ts;
  return (
    <li className="flex items-center justify-between gap-3">
      <span className={`flex items-center gap-2 ${sent ? "text-zinc-300" : "text-zinc-500"}`}>
        <span
          className={`inline-block w-1.5 h-1.5 rounded-full ${sent ? "bg-emerald-400" : "bg-zinc-700"}`}
        />
        {label}
      </span>
      <span className={`text-[11px] ${sent ? "text-emerald-300" : "text-zinc-600"}`}>
        {sent ? formatDt(ts) : "Not sent"}
      </span>
    </li>
  );
}

// ============================================================
// Month calendar view — desktop + mobile responsive
// ============================================================
const MONTH_NAMES = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const WEEKDAY_LABELS = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];

function isoFromDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function CalendarMonthView({ bookings, selectedId, onSelect, onClickDay }) {
  const today = new Date();
  const [cursor, setCursor] = useState(() => {
    // Default to the month of the next upcoming booking (or today)
    const upcoming = bookings.find((b) => b.status === "Upcoming" && b.date >= isoFromDate(today));
    if (upcoming) {
      const [y, m] = upcoming.date.split("-").map(Number);
      return new Date(y, m - 1, 1);
    }
    return new Date(today.getFullYear(), today.getMonth(), 1);
  });

  // Group bookings by ISO date for fast lookup
  const byDate = useMemo(() => {
    const m = {};
    for (const b of bookings) {
      (m[b.date] = m[b.date] || []).push(b);
    }
    // sort by time within each day
    for (const k of Object.keys(m)) m[k].sort((a, b) => a.time.localeCompare(b.time));
    return m;
  }, [bookings]);

  const monthStart = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const monthEnd = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
  // Mon=0, Sun=6 (we'll offset Sunday from JS getDay)
  const firstWeekday = (monthStart.getDay() + 6) % 7;
  const daysInMonth = monthEnd.getDate();

  // Build a 6x7 grid (some cells may be empty/padding)
  const cells = [];
  for (let i = 0; i < firstWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(cursor.getFullYear(), cursor.getMonth(), d));
  while (cells.length % 7 !== 0) cells.push(null);

  const [openDay, setOpenDay] = useState(null);

  const handleDayClick = (dateObj) => {
    if (!dateObj) return;
    const iso = isoFromDate(dateObj);
    if (!byDate[iso]) return;
    setOpenDay(openDay === iso ? null : iso);
    if (onClickDay) onClickDay(iso);
  };

  const todayIso = isoFromDate(today);

  return (
    <div className="luxury-card p-4 md:p-6" data-testid="bookings-calendar-view">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <button
          onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}
          className="btn-ghost p-2"
          data-testid="cal-prev-month"
        >
          <ChevronLeft size={16} />
        </button>
        <div className="text-center">
          <div className="eyebrow mb-1">Bookings calendar</div>
          <div className="font-serif-display text-2xl md:text-3xl">
            {MONTH_NAMES[cursor.getMonth()]} {cursor.getFullYear()}
          </div>
        </div>
        <button
          onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}
          className="btn-ghost p-2"
          data-testid="cal-next-month"
        >
          <ChevronRight size={16} />
        </button>
      </div>

      {/* Weekday header */}
      <div className="grid grid-cols-7 gap-px text-[9px] md:text-[10px] tracking-[0.18em] uppercase text-zinc-500 mb-1">
        {WEEKDAY_LABELS.map((w) => (
          <div key={w} className="text-center py-1">{w}</div>
        ))}
      </div>

      {/* Day grid */}
      <div className="grid grid-cols-7 gap-px bg-white/5" data-testid="cal-grid">
        {cells.map((d, i) => {
          if (!d) {
            return <div key={`pad-${i}`} className="bg-black/30 aspect-square sm:aspect-auto sm:h-24" />;
          }
          const iso = isoFromDate(d);
          const dayBookings = byDate[iso] || [];
          const active = dayBookings.filter((b) => b.status !== "Cancelled");
          const cancelled = dayBookings.filter((b) => b.status === "Cancelled");
          const isToday = iso === todayIso;
          const isOpen = openDay === iso;
          const isPast = iso < todayIso;

          return (
            <button
              key={iso}
              onClick={() => handleDayClick(d)}
              className={`relative bg-black text-left px-2 py-2 sm:px-2.5 sm:py-2 transition-colors aspect-square sm:aspect-auto sm:h-24 flex flex-col gap-1 ${
                dayBookings.length ? "hover:bg-white/[0.04] cursor-pointer" : "cursor-default"
              } ${isOpen ? "ring-1 ring-red-500/60 bg-red-500/[0.06]" : ""}`}
              data-testid={`cal-day-${iso}`}
              aria-label={`${iso} — ${active.length} active, ${cancelled.length} cancelled`}
            >
              <div className="flex items-center justify-between">
                <span
                  className={`text-[11px] md:text-xs font-medium ${
                    isToday ? "text-red-400" : isPast ? "text-zinc-600" : "text-zinc-300"
                  }`}
                >
                  {d.getDate()}
                </span>
                {dayBookings.length > 0 && (
                  <span className="hidden sm:inline-flex items-center gap-1 text-[9px] tracking-[0.14em] text-zinc-500 uppercase">
                    {active.length > 0 && (
                      <span className="inline-flex items-center gap-1 text-amber-300">
                        <span className="w-1 h-1 rounded-full bg-amber-400" />{active.length}
                      </span>
                    )}
                    {cancelled.length > 0 && (
                      <span className="inline-flex items-center gap-1 text-zinc-500 line-through">
                        <span className="w-1 h-1 rounded-full bg-zinc-500" />{cancelled.length}
                      </span>
                    )}
                  </span>
                )}
              </div>
              {/* Mobile: just dot count. Desktop: top 2 booking pills */}
              <div className="flex flex-wrap gap-0.5 sm:hidden">
                {dayBookings.slice(0, 4).map((b) => (
                  <span
                    key={b.id}
                    className={`w-1.5 h-1.5 rounded-full ${
                      b.status === "Cancelled" ? "bg-zinc-600" : "bg-red-500"
                    }`}
                  />
                ))}
              </div>
              <div className="hidden sm:flex flex-col gap-0.5 mt-0.5 overflow-hidden">
                {dayBookings.slice(0, 2).map((b) => (
                  <span
                    key={b.id}
                    className={`text-[10px] truncate px-1 py-0.5 border ${
                      b.status === "Cancelled"
                        ? "border-zinc-700 text-zinc-500 line-through bg-zinc-900/40"
                        : selectedId === b.id
                          ? "border-red-500 text-white bg-red-500/15"
                          : "border-amber-700/50 text-amber-200 bg-amber-500/[0.06]"
                    }`}
                    style={{ borderRadius: 0 }}
                  >
                    {b.time} · {b.name.split(" ")[0]}
                  </span>
                ))}
                {dayBookings.length > 2 && (
                  <span className="text-[9px] text-zinc-500 tracking-[0.14em] uppercase">
                    +{dayBookings.length - 2} more
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* Day-detail strip — appears when a day with bookings is clicked */}
      {openDay && byDate[openDay] && (
        <div className="mt-5 border-t border-white/10 pt-5" data-testid="cal-day-panel">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="eyebrow">Bookings on</div>
              <div className="font-serif-display text-xl">{formatDate(openDay)}</div>
            </div>
            <button onClick={() => setOpenDay(null)} className="btn-ghost p-2" data-testid="cal-close-day">
              <X size={14} />
            </button>
          </div>
          <ul className="space-y-2">
            {byDate[openDay].map((b) => {
              const cancelled = b.status === "Cancelled";
              return (
                <li
                  key={b.id}
                  onClick={() => onSelect?.(b)}
                  className={`flex items-center justify-between gap-3 border px-3 py-2.5 cursor-pointer transition ${
                    selectedId === b.id
                      ? "border-red-500/60 bg-red-500/[0.08]"
                      : "border-white/10 hover:border-white/30 hover:bg-white/[0.02]"
                  } ${cancelled ? "opacity-60" : ""}`}
                  style={{ borderRadius: 0 }}
                  data-testid={`cal-day-booking-${b.id}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span className={`text-xs font-mono ${cancelled ? "text-zinc-500 line-through" : "c-red"}`}>{b.time}</span>
                    <div className="min-w-0">
                      <div className={`text-sm truncate ${cancelled ? "text-zinc-500 line-through" : "text-white"}`}>
                        {b.name}
                      </div>
                      <div className="text-[11px] text-zinc-500 truncate">{b.business_type || "—"} · {b.business_stage || "—"}</div>
                    </div>
                  </div>
                  <span className={`inline-flex items-center gap-1.5 text-[10px] tracking-[0.12em] uppercase px-2.5 py-1 rounded-full border ${bClass(b.status)} shrink-0`}>
                    {b.status}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Legend */}
      <div className="mt-5 flex flex-wrap gap-4 text-[10px] tracking-[0.14em] uppercase text-zinc-500">
        <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-amber-400" /> Active</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-zinc-500" /> <span className="line-through">Cancelled</span></span>
        <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-red-500" /> Today</span>
      </div>
    </div>
  );
}

// ============================================================
// Booking detail panel — shared between Calendar + List views
// ============================================================
function BookingDetailPanel({
  selected, emails,
  editNotes, setEditNotes,
  editStage, setEditStage,
  savingNotes,
  updateStatus, saveStageAndNotes, onRescheduled,
}) {
  if (!selected) {
    return <div className="luxury-card p-10 text-center text-zinc-500">Select a booking to see details.</div>;
  }
  const cancelled = selected.status === "Cancelled";
  return (
    <div className="luxury-card p-7 sticky top-6" data-testid="booking-detail-panel">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="eyebrow">Booking</div>
        {selected.source === "VSL" && (
          <span
            className="inline-flex items-center text-[10px] tracking-[0.2em] uppercase px-2 py-1 border border-red-600 text-red-400 bg-red-500/10"
            style={{ borderRadius: 0 }}
            data-testid="booking-detail-source-vsl"
          >
            From VSL Funnel
          </span>
        )}
        {selected.source === "ELITE" && (
          <span
            className="inline-flex items-center text-[10px] tracking-[0.2em] uppercase px-2 py-1 border border-amber-500 text-amber-300 bg-amber-500/10"
            style={{ borderRadius: 0 }}
            data-testid="booking-detail-source-elite"
          >
            From Elite Funnel
          </span>
        )}
      </div>
      <div className={`font-serif-display text-3xl ${cancelled ? "text-zinc-500 line-through" : ""}`}>
        {selected.name}
      </div>
      <div className={`text-sm mt-1 flex items-center gap-2 ${cancelled ? "text-zinc-500 line-through" : "c-red"}`}>
        <Calendar size={13} /> {formatDate(selected.date)}
        <Clock size={13} /> {selected.time} IST
      </div>
      {cancelled && (
        <div className="mt-3 inline-flex items-center text-[10px] tracking-[0.18em] uppercase px-2 py-1 border border-zinc-700 text-zinc-400 bg-zinc-900/50" style={{ borderRadius: 0 }}>
          Cancelled · Slot re-opened for booking
        </div>
      )}
      {selected.rescheduled_from && (
        <div className="mt-2 text-[11px] text-zinc-500" data-testid="booking-rescheduled-from">Rescheduled from {selected.rescheduled_from} IST</div>
      )}
      {selected.precall_video_code && (() => {
        const opened = !!selected.precall_video_opened_at;
        const pct = selected.precall_video_watch_pct || 0;
        const tone = !opened ? "border-zinc-700 text-zinc-400 bg-zinc-900/50" : pct >= 75 ? "border-emerald-600 text-emerald-400 bg-emerald-900/20" : "border-amber-600 text-amber-300 bg-amber-900/20";
        return (
          <div className={`mt-3 inline-flex items-center gap-1.5 text-[10px] tracking-[0.14em] uppercase px-2 py-1 border ${tone}`} style={{ borderRadius: 0 }} data-testid="booking-precall-video-badge" title={opened ? `First opened ${new Date(selected.precall_video_opened_at).toLocaleString("en-IN")} · ${selected.precall_video_opens || 1} open(s)` : "Link sent, not opened yet"}>
            <Video size={11} /> Pre-call video: {opened ? `${pct}% watched` : "Not opened"}
          </div>
        );
      })()}
      <RescheduleCard booking={selected} onRescheduled={onRescheduled} />
      {selected.customer_action && (
        <div className={`mt-3 inline-flex items-center text-[10px] tracking-[0.18em] uppercase px-2 py-1 border ${selected.customer_action === "confirmed" ? "border-emerald-600 text-emerald-400 bg-emerald-900/20" : "border-amber-600 text-amber-400 bg-amber-900/20"}`} style={{ borderRadius: 0 }} data-testid="booking-customer-action">
          WhatsApp: {selected.customer_action === "confirmed" ? "Confirmed by customer" : "Reschedule requested"}
        </div>
      )}

      {(() => {
        const fit = computeFit(selected);
        if (!fit) return null;
        const bar = (label, val, max) => (
          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-[0.1em] text-zinc-500 w-16">{label}</span>
            <div className="flex-1 h-1.5 bg-white/5" style={{ borderRadius: 0 }}>
              <div className="h-full bg-red-600" style={{ width: `${(val / max) * 100}%` }} />
            </div>
            <span className="text-[10px] text-zinc-400 w-8 text-right">{val}/{max}</span>
          </div>
        );
        return (
          <div className="mt-6 border border-white/10 bg-black/30 p-4" style={{ borderRadius: 0 }} data-testid="booking-fit-card">
            <div className="flex items-center justify-between mb-3">
              <div className="eyebrow flex items-center gap-1.5"><Flame size={12} className="text-red-500" /> Lead fit score</div>
              <div className="flex items-baseline gap-2">
                <span className="font-serif-display text-3xl text-white">{fit.score}</span>
                <span className="text-xs c-muted">/100</span>
                <FitBadge fit={fit} size="lg" />
              </div>
            </div>
            <div className="space-y-2">
              {bar("Budget", fit.parts.budget, 40)}
              {bar("Goal", fit.parts.goal, 30)}
              {bar("Age", fit.parts.age, 20)}
              {bar("Revenue", fit.parts.revenue, 10)}
            </div>
          </div>
        );
      })()}

      {(selected.zoom_join_url || selected.zoom_start_url) && (
        <div className="mt-6 border border-white/10 bg-black/30 p-4" style={{ borderRadius: 0 }} data-testid="booking-zoom-card">
          <div className="eyebrow flex items-center gap-1.5 mb-3"><Video size={12} className="text-red-500" /> Zoom meeting</div>
          {selected.zoom_join_url && (
            <div className="flex items-center gap-2 mb-2">
              <a href={selected.zoom_join_url} target="_blank" rel="noreferrer" className="text-sm text-zinc-300 hover:text-white truncate flex-1" data-testid="booking-zoom-join">{selected.zoom_join_url}</a>
              <button onClick={() => { navigator.clipboard.writeText(selected.zoom_join_url); toast.success("Join link copied"); }} className="p-1.5 border border-white/10 text-zinc-400 hover:text-white" style={{ borderRadius: 0 }} data-testid="booking-zoom-copy" title="Copy join link"><Copy size={13} /></button>
            </div>
          )}
          <StartMeetingButton booking={selected} />
          <p className="mt-2 text-[11px] text-zinc-500">Join link goes to the lead; the host start link is admin-only.</p>
        </div>
      )}

      <div className="space-y-2 mt-6 text-sm">
        <div className="flex items-center gap-3 text-zinc-300"><Mail size={14} className="text-red-500" /> <a href={`mailto:${selected.email}`} className="hover:text-white">{selected.email}</a></div>
        <div className="flex items-center gap-3 text-zinc-300"><Phone size={14} className="text-red-500" /> <a href={`tel:${selected.whatsapp}`} className="hover:text-white">{selected.whatsapp}</a></div>
        {selected.business_type && <div className="flex items-center gap-3 text-zinc-300"><Briefcase size={14} className="text-red-500" /> {selected.business_type}</div>}
        {selected.business_stage && <div className="flex items-center gap-3 text-zinc-300"><Tag size={14} className="text-red-500" /> Stage: {selected.business_stage}</div>}
        {selected.business_age && <div className="flex items-center gap-3 text-zinc-300"><Hourglass size={14} className="text-red-500" /> Business age: {selected.business_age}</div>}
        {selected.city && <div className="flex items-center gap-3 text-zinc-300"><MapPin size={14} className="text-red-500" /> {selected.city}</div>}
        {selected.ad_spend && <div className="flex items-center gap-3 text-zinc-300"><Tag size={14} className="text-red-500" /> Ad spend: {selected.ad_spend}</div>}
      </div>

      {selected.diagnosis && (
        <div className="mt-5 border border-red-700/40 bg-red-900/10 p-4" data-testid="booking-detail-diagnosis">
          <div className="eyebrow mb-2 text-red-400">Growth Diagnosis</div>
          <p className="text-sm text-zinc-200 leading-relaxed">{selected.diagnosis}</p>
        </div>
      )}

      {selected.goal && (
        <div className="mt-5" data-testid="booking-detail-goal">
          <div className="eyebrow mb-2 flex items-center gap-1.5"><Target size={11} /> Revenue goal</div>
          <p className="text-sm text-zinc-300 leading-relaxed">{selected.goal}</p>
        </div>
      )}

      {selected.growth_blocker && (
        <div className="mt-5">
          <div className="eyebrow mb-2">#1 Growth blocker</div>
          <p className="text-sm text-zinc-300 leading-relaxed">{selected.growth_blocker}</p>
        </div>
      )}

      {selected.customer_source && (
        <div className="mt-5">
          <div className="eyebrow mb-2">Customers come from</div>
          <p className="text-sm text-zinc-300">{selected.customer_source}</p>
        </div>
      )}

      {selected.online_presence?.length > 0 && (
        <div className="mt-5" data-testid="booking-detail-social">
          <div className="eyebrow mb-2 flex items-center gap-1.5"><LinkIcon size={11} /> Online presence</div>
          <div className="flex flex-wrap gap-1.5">
            {selected.online_presence.map((t) => <span key={t} className="text-xs px-2 py-1 border border-white/10 text-zinc-300">{t}</span>)}
          </div>
        </div>
      )}

      {selected.prospect_outcome && (
        <div className="mt-5">
          <div className="eyebrow mb-2">When a prospect reaches out</div>
          <p className="text-sm text-zinc-300">{selected.prospect_outcome}</p>
        </div>
      )}

      {(selected.website || selected.best_social || selected.founder_linkedin) && (
        <div className="mt-5">
          <div className="eyebrow mb-2 flex items-center gap-1.5"><LinkIcon size={11} /> Website & Socials</div>
          {selected.website && <p className="text-sm text-zinc-300 break-all"><span className="text-zinc-500">Website:</span> <a href={selected.website.startsWith("http") ? selected.website : `https://${selected.website}`} target="_blank" rel="noopener noreferrer" className="text-red-400 hover:text-red-300">{selected.website}</a></p>}
          {selected.best_social && <p className="text-sm text-zinc-300 break-all mt-1"><span className="text-zinc-500">Best social:</span> <a href={selected.best_social.startsWith("http") ? selected.best_social : `https://${selected.best_social}`} target="_blank" rel="noopener noreferrer" className="text-red-400 hover:text-red-300">{selected.best_social}</a></p>}
          {selected.founder_linkedin && <p className="text-sm text-zinc-300 break-all mt-1"><span className="text-zinc-500">Founder's LinkedIn:</span> <a href={selected.founder_linkedin.startsWith("http") ? selected.founder_linkedin : `https://${selected.founder_linkedin}`} target="_blank" rel="noopener noreferrer" className="text-red-400 hover:text-red-300">{selected.founder_linkedin}</a></p>}
        </div>
      )}

      {(selected.revenue_band || selected.running_ads || selected.ad_budget) && (
        <div className="mt-5">
          <div className="eyebrow mb-2">Fit & Budget</div>
          {selected.revenue_band && <p className="text-sm text-zinc-300"><span className="text-zinc-500">Revenue:</span> {selected.revenue_band}</p>}
          {selected.running_ads && <p className="text-sm text-zinc-300 mt-1"><span className="text-zinc-500">Running ads:</span> {selected.running_ads}</p>}
          {selected.ad_budget && <p className="text-sm text-zinc-300 mt-1"><span className="text-zinc-500">Ad budget:</span> {selected.ad_budget}</p>}
        </div>
      )}

      {(selected.time_on_page != null || selected.scroll_percent != null || selected.video_watch_percent != null) && (
        <div className="mt-5" data-testid="booking-detail-engagement">
          <div className="eyebrow mb-2">Engagement · /for-coaches</div>
          {selected.time_on_page != null && <p className="text-sm text-zinc-300"><span className="text-zinc-500">Time on page:</span> {Math.floor(selected.time_on_page / 60)}m {selected.time_on_page % 60}s</p>}
          {selected.scroll_percent != null && <p className="text-sm text-zinc-300 mt-1"><span className="text-zinc-500">Scroll depth:</span> {selected.scroll_percent}%</p>}
          {selected.video_watch_percent != null && <p className="text-sm text-zinc-300 mt-1"><span className="text-zinc-500">Video watched:</span> {selected.video_watch_percent}%</p>}
        </div>
      )}

      {selected.six_month_goal && (
        <div className="mt-5">
          <div className="eyebrow mb-2">6-month goal</div>
          <p className="text-sm text-zinc-300">{selected.six_month_goal}</p>
        </div>
      )}

      {selected.social_handles && (
        <div className="mt-5">
          <div className="eyebrow mb-2 flex items-center gap-1.5"><LinkIcon size={11} /> Web / Socials</div>
          <p className="text-sm text-zinc-300 whitespace-pre-wrap break-words leading-relaxed">{selected.social_handles}</p>
        </div>
      )}

      <div className="mt-6 bg-black/30 border border-white/5 p-4">
        <div className="eyebrow mb-3">Update status</div>
        <div className="grid grid-cols-2 gap-2">
          {BOOKING_STATUSES.map((s) => (
            <button key={s} onClick={() => updateStatus(selected, s)} className={`text-xs px-3 py-2 border ${selected.status === s ? "bg-red-700 border-red-600 text-white" : "border-white/10 text-zinc-300 hover:border-white/30"}`}>
              {s}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-5 bg-black/30 border border-white/5 p-4" data-testid="booking-stage-editor">
        <div className="eyebrow mb-3">Pipeline stage</div>
        <select
          value={editStage}
          onChange={(e) => setEditStage(e.target.value)}
          className="input-luxury text-sm py-2.5 cursor-pointer w-full"
          data-testid="booking-stage-select"
        >
          <option value="" className="bg-black">— No stage —</option>
          {BOOKING_STAGES.map((s) => (
            <option key={s} value={s} className="bg-black">{s}</option>
          ))}
        </select>

        <div className="eyebrow mt-4 mb-2">Notes</div>
        <textarea
          rows={5}
          value={editNotes}
          onChange={(e) => setEditNotes(e.target.value)}
          placeholder="Call notes, objections, next steps, payment link sent, follow-up date…"
          className="input-luxury text-sm resize-none w-full"
          data-testid="booking-notes-input"
        />

        <button
          onClick={saveStageAndNotes}
          disabled={savingNotes}
          className="btn-primary w-full justify-center text-sm py-2.5 mt-3 disabled:opacity-60"
          data-testid="booking-save-notes-btn"
        >
          <Save size={14} /> {savingNotes ? "Saving…" : "Save stage & notes"}
        </button>
      </div>

      <div className="mt-5 bg-black/30 border border-white/5 p-4" data-testid="booking-email-timeline">
        <div className="eyebrow mb-3 flex items-center gap-1.5"><Mail size={11} /> Emails sent</div>
        {emails == null ? (
          <div className="text-xs text-zinc-500">Loading…</div>
        ) : emails.error ? (
          <div className="text-xs text-rose-400">Could not load email history.</div>
        ) : (
          <ul className="text-xs space-y-2">
            <EmailRow label="Confirmation (to lead)" ts={emails.confirmation_sent_at} />
            <EmailRow label="Admin notification" ts={emails.admin_notification_sent_at} />
            <EmailRow label="24-h reminder" ts={emails.reminder_sent_at} />
            <EmailRow label="Post-call follow-up" ts={emails.followup_sent_at} />
            <li className="pt-2 mt-2 border-t border-white/5">
              <div className="flex items-center justify-between">
                <span className="text-zinc-400">7-day drip campaign</span>
                {emails.drip?.unsubscribed && (
                  <span className="text-[9px] tracking-[0.18em] uppercase text-rose-400 border border-rose-700/40 px-1.5 py-0.5">Unsubscribed</span>
                )}
              </div>
              {emails.drip?.sent_days?.length > 0 ? (
                <div className="flex flex-wrap gap-1 mt-1.5">
                  {[1, 2, 3, 4, 5, 6, 7].map((d) => {
                    const sent = emails.drip.sent_days.includes(d);
                    return (
                      <span
                        key={d}
                        className={`inline-flex items-center justify-center w-7 h-6 text-[10px] border ${
                          sent ? "bg-emerald-900/30 border-emerald-700/50 text-emerald-300" : "border-white/10 text-zinc-600"
                        }`}
                        style={{ borderRadius: 0 }}
                        title={sent ? `Day ${d} sent` : `Day ${d} pending`}
                      >
                        D{d}
                      </span>
                    );
                  })}
                </div>
              ) : (
                <div className="text-[11px] text-zinc-500 mt-1">No drip emails sent yet.</div>
              )}
            </li>
          </ul>
        )}
      </div>
    </div>
  );
}

export function BookingsAdmin({ onAuthFail }) {  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("All");
  const [selected, setSelected] = useState(null);
  const [emails, setEmails] = useState(null);
  const [editNotes, setEditNotes] = useState("");
  const [editStage, setEditStage] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [view, setView] = useState("calendar"); // 'calendar' | 'list'
  const [sortBy, setSortBy] = useState("date"); // 'date' | 'fit'

  const sortedBookings = useMemo(() => {
    if (sortBy !== "fit") return bookings;
    return [...bookings].sort((a, b) => ((computeFit(b)?.score ?? -1) - (computeFit(a)?.score ?? -1)));
  }, [bookings, sortBy]);

  // Sync editor state + fetch email timeline when a row is selected
  useEffect(() => {
    if (!selected) {
      setEmails(null);
      return;
    }
    setEditNotes(selected.notes || "");
    setEditStage(selected.stage || "");
    let cancelled = false;
    setEmails(null);
    axios
      .get(`${API}/admin/bookings/${selected.id}/emails`, { headers: authHeaders() })
      .then((r) => { if (!cancelled) setEmails(r.data); })
      .catch(() => { if (!cancelled) setEmails({ error: true }); });
    return () => { cancelled = true; };
  }, [selected]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = statusFilter !== "All" ? `?status_filter=${encodeURIComponent(statusFilter)}` : "";
      const [b, s] = await Promise.all([
        axios.get(`${API}/admin/bookings${qs}`, { headers: authHeaders() }),
        axios.get(`${API}/admin/bookings/stats`, { headers: authHeaders() }),
      ]);
      setBookings(b.data);
      setStats(s.data);
    } catch (err) {
      if (err?.response?.status === 401) onAuthFail?.();
      else toast.error("Failed to load bookings");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, onAuthFail]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (b, newStatus) => {
    try {
      const { data } = await axios.patch(`${API}/admin/bookings/${b.id}`, { status: newStatus }, { headers: authHeaders() });
      setBookings((prev) => prev.map((x) => x.id === data.id ? data : x));
      if (selected?.id === b.id) setSelected(data);
      toast.success(`Status → ${newStatus}`);
    } catch {
      toast.error("Could not update");
    }
  };

  const onRescheduled = (data) => {
    setBookings((prev) => prev.map((x) => x.id === data.id ? data : x));
    setSelected(data);
  };

  const saveStageAndNotes = async () => {
    if (!selected) return;
    setSavingNotes(true);
    try {
      const { data } = await axios.patch(
        `${API}/admin/bookings/${selected.id}`,
        { stage: editStage, notes: editNotes },
        { headers: authHeaders() }
      );
      setBookings((prev) => prev.map((x) => x.id === data.id ? data : x));
      setSelected(data);
      toast.success("Saved");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not save";
      toast.error(typeof msg === "string" ? msg : "Could not save");
    } finally {
      setSavingNotes(false);
    }
  };

  const exportCsv = () => {
    if (!bookings.length) return toast.error("Nothing to export");
    const headers = ["Date", "Time (IST)", "Name", "WhatsApp", "Email", "City", "Business Type", "Business Stage", "Business Age", "Revenue Goal", "Fit Score", "Customers From", "Growth Blocker", "Online Presence", "Prospect Outcome", "Ad Spend", "6-Month Goal", "Website", "Best Social", "Founder LinkedIn", "Revenue", "Running Ads", "Ad Budget", "Time on Page (s)", "Scroll %", "Video Watch %", "Diagnosis", "Source", "Status", "Stage", "Notes"];
    const rows = bookings.map((b) => [b.date, b.time, b.name, b.whatsapp, b.email, b.city || "", b.business_type || "", b.business_stage || "", b.business_age || "", b.goal || "", (computeFit(b)?.score ?? ""), b.customer_source || "", b.growth_blocker || "", (b.online_presence || []).join("; "), b.prospect_outcome || "", b.ad_spend || "", b.six_month_goal || "", b.website || "", b.best_social || "", b.founder_linkedin || "", b.revenue_band || "", b.running_ads || "", b.ad_budget || "", b.time_on_page ?? "", b.scroll_percent ?? "", b.video_watch_percent ?? "", (b.diagnosis || "").replace(/\n/g, " "), b.source || "", b.status, b.stage || "", (b.notes || "").replace(/\n/g, " ")]);
    const csv = [headers, ...rows].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `brandchilly-bookings-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="bookings-admin">
      <WhatsAppFlowStatus />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {[
          { label: "Total Bookings", value: stats.total || 0 },
          { label: "Upcoming", value: stats.upcoming || 0 },
          { label: "This Week", value: stats.this_week || 0 },
          { label: "Show Rate", value: `${stats.show_rate || 0}%` },
        ].map((s) => (
          <div key={s.label} className="luxury-card p-4">
            <div className="text-[11px] tracking-[0.18em] uppercase text-zinc-500">{s.label}</div>
            <div className="font-serif-display text-3xl mt-1">{s.value}</div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex flex-wrap items-center gap-2">
          {["All", ...BOOKING_STATUSES].map((s) => (
            <button key={s} onClick={() => setStatusFilter(s)} className={`text-xs px-3 py-1.5 rounded-full border ${statusFilter === s ? "bg-red-500 border-red-500 text-white" : "bg-zinc-900/50 border-white/10 text-zinc-400 hover:text-white"}`} data-testid={`booking-filter-${s.toLowerCase()}`}>{s}</button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          {view === "list" && (
            <div className="inline-flex border border-white/10 overflow-hidden" data-testid="bookings-sort-toggle">
              <button
                onClick={() => setSortBy("date")}
                className={`text-xs px-3 py-2 ${sortBy === "date" ? "bg-red-500 text-white" : "bg-transparent text-zinc-400 hover:text-white"}`}
                data-testid="bookings-sort-date"
              >
                Date
              </button>
              <button
                onClick={() => setSortBy("fit")}
                className={`text-xs px-3 py-2 inline-flex items-center gap-1.5 border-l border-white/10 ${sortBy === "fit" ? "bg-red-500 text-white" : "bg-transparent text-zinc-400 hover:text-white"}`}
                data-testid="bookings-sort-fit"
              >
                <Flame size={12} /> Fit
              </button>
            </div>
          )}
          <div className="inline-flex border border-white/10 overflow-hidden" data-testid="bookings-view-toggle">
            <button
              onClick={() => setView("calendar")}
              className={`text-xs px-3 py-2 inline-flex items-center gap-1.5 ${view === "calendar" ? "bg-red-500 text-white" : "bg-transparent text-zinc-400 hover:text-white"}`}
              data-testid="bookings-view-calendar"
            >
              <LayoutGrid size={13} /> Calendar
            </button>
            <button
              onClick={() => setView("list")}
              className={`text-xs px-3 py-2 inline-flex items-center gap-1.5 border-l border-white/10 ${view === "list" ? "bg-red-500 text-white" : "bg-transparent text-zinc-400 hover:text-white"}`}
              data-testid="bookings-view-list"
            >
              <ListIcon size={13} /> List
            </button>
          </div>
          <button onClick={load} className="btn-ghost text-sm py-2 px-3" disabled={loading}>
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
          <button onClick={exportCsv} className="btn-ghost text-sm py-2 px-3"><Download size={14} /> CSV</button>
        </div>
      </div>

      {loading ? <div className="luxury-card p-12 text-center text-zinc-500">Loading…</div> : bookings.length === 0 ? (
        <div className="luxury-card p-12 text-center">
          <div className="font-serif-display text-2xl">No bookings yet.</div>
          <p className="text-zinc-500 mt-2 text-sm">Bookings show up here as soon as someone confirms a slot.</p>
        </div>
      ) : view === "calendar" ? (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
          <div className="lg:col-span-7">
            <CalendarMonthView bookings={bookings} selectedId={selected?.id} onSelect={setSelected} />
          </div>
          <div className="lg:col-span-5">
            <BookingDetailPanel
              selected={selected}
              emails={emails}
              editNotes={editNotes}
              setEditNotes={setEditNotes}
              editStage={editStage}
              setEditStage={setEditStage}
              savingNotes={savingNotes}
              updateStatus={updateStatus}
              saveStageAndNotes={saveStageAndNotes}
              onRescheduled={onRescheduled}
            />
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
          <div className="lg:col-span-7 luxury-card p-2">
            <div className="overflow-x-auto">
              <table className="w-full text-sm font-body">
                <thead className="text-[11px] tracking-[0.2em] uppercase text-zinc-500">
                  <tr className="border-b border-white/5">
                    <th className="text-left px-4 py-3">Date · Time</th>
                    <th className="text-left px-4 py-3">Name</th>
                    <th className="text-left px-4 py-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedBookings.map((b) => {
                    const cancelled = b.status === "Cancelled";
                    return (
                    <tr key={b.id} onClick={() => setSelected(b)} className={`border-b border-white/5 cursor-pointer transition-colors ${selected?.id === b.id ? "bg-red-500/5" : "hover:bg-white/[0.02]"} ${cancelled ? "opacity-60" : ""}`} data-testid={`booking-row-${b.id}`}>
                      <td className="px-4 py-4 text-zinc-300 whitespace-nowrap">
                        <div className={cancelled ? "text-zinc-500 line-through" : "text-white"}>{formatDate(b.date)}</div>
                        <div className={`text-xs mt-0.5 ${cancelled ? "text-zinc-600 line-through" : "c-red"}`}>{b.time} IST</div>
                      </td>
                      <td className="px-4 py-4">
                        <div className={`font-medium flex items-center gap-2 flex-wrap ${cancelled ? "text-zinc-500 line-through" : "text-white"}`}>{b.name}
                          {b.source === "VSL" && (
                            <span
                              className="inline-flex items-center text-[9px] tracking-[0.18em] uppercase px-1.5 py-0.5 border border-red-600 text-red-400 bg-red-500/10"
                              style={{ borderRadius: 0 }}
                              data-testid={`booking-source-vsl-${b.id}`}
                            >
                              VSL
                            </span>
                          )}
                          {b.source === "ELITE" && (
                            <span
                              className="inline-flex items-center text-[9px] tracking-[0.18em] uppercase px-1.5 py-0.5 border border-amber-500 text-amber-300 bg-amber-500/10"
                              style={{ borderRadius: 0 }}
                              data-testid={`booking-source-elite-${b.id}`}
                            >
                              ELITE
                            </span>
                          )}
                          <FitBadge fit={computeFit(b)} />
                          {b.precall_video_code && (
                            <span
                              className={`inline-flex items-center gap-1 text-[9px] tracking-[0.12em] uppercase px-1.5 py-0.5 border ${!b.precall_video_opened_at ? "border-zinc-700 text-zinc-500" : (b.precall_video_watch_pct || 0) >= 75 ? "border-emerald-600 text-emerald-400 bg-emerald-900/20" : "border-amber-600 text-amber-300 bg-amber-900/20"}`}
                              style={{ borderRadius: 0 }}
                              title={b.precall_video_opened_at ? `Pre-call video ${b.precall_video_watch_pct || 0}% watched` : "Pre-call video not opened"}
                              data-testid={`booking-row-video-${b.id}`}
                            >
                              <Video size={9} /> {b.precall_video_opened_at ? `${b.precall_video_watch_pct || 0}%` : "—"}
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] text-zinc-500">{b.business_type || "—"} · {b.business_stage || "—"}</div>
                      </td>
                      <td className="px-4 py-4">
                        <span className={`inline-flex items-center gap-1.5 text-[10px] tracking-[0.12em] uppercase px-2.5 py-1 rounded-full border ${bClass(b.status)}`}>
                          <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />
                          {b.status}
                        </span>
                        {b.stage && (
                          <div className="mt-1.5">
                            <span
                              className={`inline-flex items-center text-[9px] tracking-[0.16em] uppercase px-2 py-0.5 border ${stageClass(b.stage)}`}
                              style={{ borderRadius: 0 }}
                              data-testid={`booking-stage-${b.id}`}
                            >
                              {b.stage}
                            </span>
                          </div>
                        )}
                        <div className="mt-1.5"><StartMeetingButton booking={b} compact /></div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="lg:col-span-5">
            <BookingDetailPanel
              selected={selected}
              emails={emails}
              editNotes={editNotes}
              setEditNotes={setEditNotes}
              editStage={editStage}
              setEditStage={setEditStage}
              savingNotes={savingNotes}
              updateStatus={updateStatus}
              saveStageAndNotes={saveStageAndNotes}
              onRescheduled={onRescheduled}
            />
          </div>
        </div>
      )}
    </div>
  );
}

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export function AvailabilityAdmin({ onAuthFail }) {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newBlocked, setNewBlocked] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/booking-settings`, { headers: authHeaders() });
      setSettings(data);
    } catch (err) {
      if (err?.response?.status === 401) onAuthFail?.();
      else toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  }, [onAuthFail]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/admin/booking-settings`, {
        working_days: settings.working_days,
        start_time: settings.start_time,
        end_time: settings.end_time,
        slot_minutes: settings.slot_minutes,
        buffer_minutes: settings.buffer_minutes,
        min_notice_minutes: settings.min_notice_minutes,
        rolling_window_days: settings.rolling_window_days,
      }, { headers: authHeaders() });
      setSettings({ ...data, blocked_dates: settings.blocked_dates });
      toast.success("Saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const toggleDay = (i) => {
    setSettings((s) => ({ ...s, working_days: s.working_days.includes(i) ? s.working_days.filter((x) => x !== i) : [...s.working_days, i].sort() }));
  };

  const addBlocked = async () => {
    if (!newBlocked) return;
    try {
      await axios.post(`${API}/admin/booking-settings/blocked-dates`, { date: newBlocked }, { headers: authHeaders() });
      setSettings((s) => ({ ...s, blocked_dates: [...(s.blocked_dates || []), newBlocked].sort() }));
      setNewBlocked("");
      toast.success("Date blocked");
    } catch {
      toast.error("Could not block date");
    }
  };

  const removeBlocked = async (d) => {
    try {
      await axios.delete(`${API}/admin/booking-settings/blocked-dates/${d}`, { headers: authHeaders() });
      setSettings((s) => ({ ...s, blocked_dates: (s.blocked_dates || []).filter((x) => x !== d) }));
    } catch {
      toast.error("Could not unblock");
    }
  };

  if (loading || !settings) return <div className="luxury-card p-12 text-center text-zinc-500">Loading…</div>;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="availability-admin">
      <div className="luxury-card p-7 space-y-5">
        <div>
          <div className="eyebrow mb-3">Working days</div>
          <div className="flex flex-wrap gap-2">
            {DAYS.map((d, i) => {
              const active = settings.working_days.includes(i);
              return (
                <button key={d} onClick={() => toggleDay(i)} className={`px-3 py-2 text-xs border ${active ? "bg-red-700 border-red-600 text-white" : "border-white/10 text-zinc-400"}`}>
                  {d}
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="eyebrow mb-2">Start time (IST)</div>
            <input type="time" value={settings.start_time} onChange={(e) => setSettings({ ...settings, start_time: e.target.value })} className="input-luxury text-sm" />
          </div>
          <div>
            <div className="eyebrow mb-2">End time (IST)</div>
            <input type="time" value={settings.end_time} onChange={(e) => setSettings({ ...settings, end_time: e.target.value })} className="input-luxury text-sm" />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="eyebrow mb-2">Slot length (min)</div>
            <input type="number" min="10" max="240" value={settings.slot_minutes} onChange={(e) => setSettings({ ...settings, slot_minutes: parseInt(e.target.value || 0) })} className="input-luxury text-sm" />
          </div>
          <div>
            <div className="eyebrow mb-2">Buffer (min)</div>
            <input type="number" min="0" max="120" value={settings.buffer_minutes} onChange={(e) => setSettings({ ...settings, buffer_minutes: parseInt(e.target.value || 0) })} className="input-luxury text-sm" />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="eyebrow mb-2">Min notice (minutes)</div>
            <input type="number" min="0" max="1440" value={settings.min_notice_minutes} onChange={(e) => setSettings({ ...settings, min_notice_minutes: parseInt(e.target.value || 0) })} className="input-luxury text-sm" data-testid="settings-min-notice" />
          </div>
          <div>
            <div className="eyebrow mb-2">Rolling window (days)</div>
            <input type="number" min="1" max="180" value={settings.rolling_window_days} onChange={(e) => setSettings({ ...settings, rolling_window_days: parseInt(e.target.value || 0) })} className="input-luxury text-sm" />
          </div>
        </div>

        <button onClick={save} disabled={saving} className="btn-primary w-full justify-center text-sm py-2.5 disabled:opacity-60">
          <Save size={14} /> {saving ? "Saving…" : "Save settings"}
        </button>
      </div>

      <div className="luxury-card p-7">
        <div className="eyebrow mb-3">Blocked dates (holidays / days off)</div>
        <p className="text-xs text-zinc-500 mb-4">No slots will be shown for these dates.</p>
        <div className="flex gap-2 mb-4">
          <input type="date" value={newBlocked} onChange={(e) => setNewBlocked(e.target.value)} className="input-luxury text-sm flex-1" />
          <button onClick={addBlocked} className="btn-primary text-sm py-2.5 px-4"><Plus size={14} /> Block</button>
        </div>
        {(settings.blocked_dates || []).length === 0 ? (
          <p className="text-sm text-zinc-500 text-center py-6">No blocked dates.</p>
        ) : (
          <ul className="space-y-2">
            {(settings.blocked_dates || []).map((d) => (
              <li key={d} className="flex items-center justify-between border border-white/5 px-3 py-2">
                <span className="text-sm text-white">{formatDate(d)}</span>
                <button onClick={() => removeBlocked(d)} className="text-zinc-500 hover:text-red-500"><X size={15} /></button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
